import type { KnowledgeBaseArticle } from "@noverik/contracts";
import { ArrowLeft, BookOpen, Paperclip, Tag } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { apiRequest, ApiRequestError } from "../lib/api";

export function KnowledgeBaseArticlePage() {
  const { articleId = "" } = useParams();
  const [article, setArticle] = useState<KnowledgeBaseArticle | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const payload = await apiRequest<KnowledgeBaseArticle>(
        "/agent/knowledge-base/" + encodeURIComponent(articleId),
      );
      setArticle(payload);
    } catch (err) {
      setError(
        err instanceof ApiRequestError
          ? err.message
          : "Não foi possível carregar o artigo.",
      );
    } finally {
      setLoading(false);
    }
  }, [articleId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) {
    return <div className="page"><div className="data-message">Carregando artigo...</div></div>;
  }

  if (error || !article) {
    return (
      <div className="page">
        <Link className="back-link" to="/desk/base"><ArrowLeft size={16} /> Voltar à base</Link>
        <div className="data-message error">{error || "Artigo não encontrado."}</div>
      </div>
    );
  }

  return (
    <div className="page">
      <Link className="back-link" to="/desk/base"><ArrowLeft size={16} /> Voltar à base</Link>

      <article className="kb-article">
        <header>
          <div className="kb-article-icon"><BookOpen size={22} /></div>
          <div>
            <span className="eyebrow">{article.category?.name || "BASE DE CONHECIMENTO"}</span>
            <h1>{article.question}</h1>
            <div className="kb-article-meta">
              <span className={article.published ? "status status-open" : "status status-closed"}>
                {article.visibility}
              </span>
              {article.attachments > 0 && <span><Paperclip size={14} /> {article.attachments} anexo{article.attachments === 1 ? "" : "s"}</span>}
            </div>
          </div>
        </header>

        <div className="kb-answer">{article.answerText}</div>

        {(article.topics.length > 0 || article.keywords) && (
          <footer>
            <Tag size={15} />
            <div>
              {article.topics.map((topic) => <span key={topic} className="kb-tag">{topic}</span>)}
              {article.keywords && <span className="kb-keywords">{article.keywords}</span>}
            </div>
          </footer>
        )}
      </article>
    </div>
  );
}
